import {
  BubbleButton,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
} from '@go-client/components/BubbleUI';

import React, { Fragment, useEffect, useState } from 'react';

import { FormGenericContainer } from '@go-client/components/FormGenericContainer';
import { mapBy } from '@go-shared/lib/functional';
import { Time } from '@go-shared/types/time';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { canEditItineraryInfo, canEditSailingInfo } from '@go-shared/lib/permissions';
import {
  BareSimpleSailing,
  BareSimpleSailingDay,
  SailingDayLocation,
  SailingDayType,
} from '@go-shared/types/sailing';
import { unsafeDeepCopy } from '@go-shared/lib/utils';

interface EditDayFormProps {
  dayToEdit: EditDaySidePanel;
  hideItineraryInfo?: boolean;
  onSubmit: (dayEditForm: EditDayForm) => void;
  onClose: () => void;
  onEditCanvas?: () => void;
  onEditFeed?: () => void;
}

export function generateDayEditForm(day?: BareSimpleSailingDay): EditDayForm {
  return {
    type: day?.type ?? 'port',
    locations: day?.locations ? unsafeDeepCopy(day.locations) : [],
    dayTheme: day?.dayTheme,
    _dirty: false,
  };
}

export interface EditDayForm {
  type: SailingDayType;
  dayTheme?: string;
  locations: SailingDayLocation[];
  _dirty: boolean;
}

export interface EditDaySidePanel {
  sailing: BareSimpleSailing;
  day: BareSimpleSailingDay;
  dayIndex: number;
}

export function EditDayForm(props: EditDayFormProps) {
  const { user } = useUserInfo();
  const serverInfo = useServerInfo();

  const { brand } = serverInfo;
  const [dayEditForm, setDayEditForm] = useState<EditDayForm>(generateDayEditForm());

  const dayThemeOpts = mapBy(brand.dayThemes, '_id');

  useEffect(() => {
    setDayEditForm(generateDayEditForm(props.dayToEdit?.day));
  }, [props.dayToEdit]);

  function changeDayEditForm<T extends keyof EditDayForm>(key: T, val: EditDayForm[T]) {
    setDayEditForm({ ...dayEditForm, _dirty: true, [key]: val });
  }

  function changeDayEditFormLocation(
    locIdx: number,
    name: string,
    arrival: Time | undefined,
    departure: Time | undefined
  ) {
    const locationCopy = [...dayEditForm.locations];

    locationCopy[locIdx].name = name;
    locationCopy[locIdx].arrival = arrival;
    locationCopy[locIdx].departure = departure;

    setDayEditForm({
      ...dayEditForm,
      locations: locationCopy,
      _dirty: true,
    });
  }

  function handleResetDayEditForm() {
    setDayEditForm(generateDayEditForm(props.dayToEdit.day));
  }

  return (
    <BubbleDrawer containerClassName="bg-eggshell" onClose={props.onClose}>
      <FormGenericContainer
        footer={() =>
          canEditSailingInfo(user) ? (
            <>
              <BubbleButton
                variant="navy"
                onClick={handleResetDayEditForm}
                inverse
                className="ml-auto"
                disabled={!dayEditForm._dirty}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
              <BubbleButton
                variant="navy"
                onClick={() => props.onSubmit(dayEditForm)}
                disabled={!dayEditForm._dirty}
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
            </>
          ) : (
            <></>
          )
        }
      >
        <div className="flex items-start">
          <div>
            <h1 className="text-lg text-slate-dark font-semibold">
              {props.dayToEdit.sailing.name} (Day: {props.dayToEdit.day.name})
            </h1>
            <h2 className="text-2xl text-navy">Day Details</h2>
          </div>
          <BubbleButton
            onClick={props.onClose}
            className="ml-auto -mt-3"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div className="mt-8">
          <div className="grid grid-cols-1 gap-6">
            <div className="canvas-link relative">
              <div className="canvas-link-image" />
              <div
                className="absolute top-0 left-0 w-full h-full flex flex-col
         justify-end items-start"
              >
                <div className="flex ml-3 mb-3">
                  <BubbleButton
                    onClick={props.onEditCanvas}
                    className="shadow-md border border-white-darkest canvas-button mr-3"
                    variant="white"
                    icon={['fass', 'chart-tree-map']}
                  >
                    Daily Planner
                  </BubbleButton>
                  <BubbleButton
                    onClick={props.onEditFeed}
                    className="shadow-md border border-white-darkest canvas-button"
                    variant="white"
                    icon={['fass', 'newspaper']}
                  >
                    Feed Editor
                  </BubbleButton>
                </div>
              </div>
            </div>
            {canEditSailingInfo(user) && (
              <div className="grid grid-cols-4 gap-4">
                <BubbleFormGroup label="Day Theme" className="col-span-4">
                  <BubbleSelect
                    block
                    allowUndefined
                    labelExtractor={id => dayThemeOpts[id].name}
                    value={dayEditForm.dayTheme}
                    options={brand.dayThemes.map(s => s._id)}
                    onValue={val => changeDayEditForm('dayTheme', val)}
                  />
                </BubbleFormGroup>
                {!props.hideItineraryInfo && canEditItineraryInfo(user, serverInfo) && (
                  <>
                    <BubbleFormGroup label="Type" className="col-span-4">
                      <BubbleSelect
                        block
                        allowUndefined={false}
                        value={dayEditForm.type}
                        options={['port', 'sea']}
                        onValue={val => changeDayEditForm('type', val)}
                      />
                    </BubbleFormGroup>
                    <hr className="w-full my-3 col-span-4" />
                    {dayEditForm.locations.map((loc, i) => (
                      <Fragment key={i}>
                        <BubbleFormGroup label="Location" className="col-span-2">
                          <BubbleInput
                            type="text"
                            block
                            value={loc.name}
                            onValue={val =>
                              changeDayEditFormLocation(i, val, loc.arrival, loc.departure)
                            }
                          />
                        </BubbleFormGroup>
                        <div className="col-span-2 flex items-end">
                          <BubbleFormGroup label="Arrival" className="flex-grow mr-2">
                            <BubbleInput
                              type="time"
                              block
                              value={loc.arrival}
                              onValue={val =>
                                changeDayEditFormLocation(i, loc.name, val, loc.departure)
                              }
                            />
                          </BubbleFormGroup>
                          <BubbleFormGroup label="Departure" className="flex-grow ml-2">
                            <BubbleInput
                              type="time"
                              block
                              value={loc.departure}
                              onValue={val =>
                                changeDayEditFormLocation(i, loc.name, loc.arrival, val)
                              }
                            />
                          </BubbleFormGroup>
                          <BubbleButton
                            variant="crimson"
                            inverse
                            icon="trash-alt"
                            onClick={() => {
                              const newVal = [...dayEditForm.locations];
                              newVal.splice(i, 1);
                              changeDayEditForm('locations', newVal);
                            }}
                          />
                        </div>
                      </Fragment>
                    ))}
                    <div className="col-span-4 -mt-2">
                      <BubbleButton
                        variant="navy"
                        inverse
                        className="p-0-i"
                        onClick={() =>
                          changeDayEditForm('locations', [...dayEditForm.locations, { name: '' }])
                        }
                        icon="plus"
                      >
                        Add another location
                      </BubbleButton>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </FormGenericContainer>
    </BubbleDrawer>
  );
}
